import _ from "lodash";
import { colors } from "@toddle-edu/ds-theme";
import {
  AddCircleOutlined,
  ExpandArrowsTwoOutlined,
  EyeOutlined,
  PencilLineOutlined,
} from "@toddle-edu/ds-icons";
import ACLStore from "lib/aclStore";
import ACLStorage from "lib/aclService";
import moment from "moment";
import {
  DAYS_IN_ORDER,
  getMemoizedFunction,
  getIsStudentPronounsEnabled,
  parseConfigurationValue,
} from "Utils";
import { validateEmail } from "@toddle-edu/common-utils";

import {
  CURRICULUM_TYPE_DP,
  CURRICULUM_TYPE_MYP,
  CURRICULUM_TYPE_PYP,
  CURRICULUM_TYPE_UBD,
  STAFF,
  STUDENT_PROFILE_CUSTOM_FIELDS_CONFIG,
} from "Constants/stringConstants";
import { DATE_TIME_CONSTANTS } from "Constants/dateTimeConstants";
import {
  ENABLE_STUDENT_MIDDLE_NAME,
  ENABLE_ROLES_AND_PERMISSIONS_CONSUMPTION,
  ADMIN_PORTAL_ACCESS_PERMISSION,
  CAN_VIEW_STUDENT_PROFILE,
  ENABLE_IBECOURSEWORK,
  CAN_VIEW_AND_MANAGE_STUDENT_PROJECTS,
  ENABLE_STUDENT_PROFILE_UI_REVAMP,
  ENABLE_SECOND_NUMBER_IN_CONTACT_INFO,
  ENABLE_THIRD_NUMBER_IN_CONTACT_INFO,
  DISABLE_FAMILY_INVITE_OPTIONS,
  BEHAVIOUR_RNP_ACCOUNT_LEVEL_MODULE_SETTING,
  CAN_VIEW_BEHAVIOUR_TAB_FOR_ALL_STUDENTS_PROGRAMME,
  BEHAVIOUR_RNP_PROGRAMME_LEVEL_MODULE_SETTING,
  CAN_VIEW_STUDENT_GOALS,
  BEHAVIOUR_DASHBOARD_ACCESS_PERMISSION,
  ENABLE_STUDENT_TIMETABLE_PERMISSION,
} from "Constants/permissionConstants";
import {
  PROJECT_GROUP_TYPE_COMMUNITY,
  PROJECT_GROUP_TYPE_PERSONAL,
  PROJECT_GROUP_TYPE_SERVICE_AS_ACTON,
} from "Projects/Constants/stringConstants";
import {
  getAccessLevels,
  MODULES,
  RULE_OPTIONS,
  getIsAdminByRole,
  hasModuleAdminAccess,
} from "lib/staffPermissionUtils";
import { getOrganizationId } from "modules/CommonSelectors";
import { STUDENT_PROGRESS_SUMMARY_PERMISSION_MAPPING } from "StudentProgressSummary/modules/Utils";
import { PROFILE_AVATAR_COLORS } from "Constants/colorConstants";
import { getUserInfo } from "Login/modules/LoginModule/LoginUtils";
import { getUserCoursesFilters } from "Course/modules/CourseModule";
import { getUserCourseTagsFilters } from "Courses/modules/utils";
import { getProjectGroupContexts } from "Platform/modules/PlatformUtils";
import { getOrganizationConstantsFromCache } from "./ProfileDetailsGraphqlHelpers";
import { BEHAVIOUR_FILTER_TYPE } from "BehaviourManagement/modules/Utils";
import { getIsBehaviourRnPModuleConsumptionEnabled } from "Utils/ACLStoreUtils";
import { getIsBehaviourModuleEnabledInModuleSettingsAndRnP } from "Utils/BehaviourUtils";

//-- Student profile RnP permission/module settings --//
export const STUDENT_PROFILE_CONTROL_PARAMETER = {
  MODULE_SETTING: CAN_VIEW_STUDENT_PROFILE,
  VIEW_DOWNLOAD_SIGN_IN_AND_FAMILY_INVITE_CODE:
    "StudentProfile:CanViewDownloadStudentSignInAndFamilyInviteCode",
  VIEW_BASIC_DETAILS: "StudentProfile:CanViewBasicDetailsAboutStudent",
  VIEW_OTHER_DETAILS: "StudentProfile:CanViewOtherStudentDetails",
  VIEW_ATTENDANCE_AND_HOMEROOM: "StudentProfile:CanViewStudentAttendance",
  UPDATE_DETAILS: "StudentProfile:CanUpdateStudentDetails",
  UPDATE_HOMEROOM_AND_CLASSES:
    "StudentProfile:CanUpdateStudentClassesAndHomeroomAdvisor",
  VIEW_AND_UPDATE_STUDENT_FILES: "Module:StudentProfileStudentFiles",
  VIEW_TEACHER_NOTES: "Module:StudentProfileTeacherNotes",
  UPDATE_TEACHER_NOTES: "StudentProfileTeacherNotes:CanManageTeacherNotes",
  VIEW_OTHER_INFO: "Module:StudentProfileOtherInfo",
  UPDATE_OTHER_INFO: "StudentProfileOtherInfo:CanManageOtherInfo",
  MANAGE_EXCUSALS: "StudentProfile:CanManageExcusals",
  VIEW_ASSIGNMENTS_AND_GRADEBOOK:
    "StudentProfile:CanViewAssignmentsAndGradebooksForStudent",
  VIEW_PORTFOLIO_POST: "StudentProfile:CanViewPortfolioPostsForStudent",
  VIEW_GOALS: "StudentProfile:CanViewGoalsForStudent",
  VIEW_PROGRESS_REPORTS: "StudentProfile:CanViewProgressReportsForStudent",
  VIEW_CALENDAR: "StudentProfile:CanViewCalendarForStudent",
  VIEW_PROJECTS_AND_COURSEWORK: "StudentProfile:CanViewProjectsAndCoursework",
  VIEW_TIMETABLE: "StudentProfile:CanViewTimetableForStudents",
  VIEW_BEHAVIOUR_INCIDENTS:
    "StudentProfile:CanViewBehaviourIncidentsForStudent",
  VIEW_STUDENT_FLAGS: "Module:ViewStudentFlags",
  UPDATE_STUDENT_FLAGS: "ViewStudentFlags:CanManageStudentFlags",
  VIEW_BEHAVIOUR_TAB_FOR_ALL_STUDENTS_ACCOUNT: BEHAVIOUR_RNP_ACCOUNT_LEVEL_MODULE_SETTING,
  VIEW_BEHAVIOUR_TAB_FOR_ALL_STUDENTS_PROGRAMME: CAN_VIEW_BEHAVIOUR_TAB_FOR_ALL_STUDENTS_PROGRAMME,
  VIEW_BEHAVIOUR_TAB_FOR_THEIR_STUDENTS_PROGRAMME: BEHAVIOUR_RNP_PROGRAMME_LEVEL_MODULE_SETTING,
};

// Module 'controlParameterObj' for CombinedAccessBlocker
export const STUDENT_PORTFOLIO_CONTROL_PARAMETER = {
  type: "MODULE_SETTINGS",
  controlParameter: STUDENT_PROFILE_CONTROL_PARAMETER.MODULE_SETTING,
};

// Helper function to check if user can access behaviour tab in student profile when behaviour rnp consumption is ON
const getHasAccessToBehaviourTabFromBehaviourRnP = ({
  isStaffAssociatedWithStudent = false,
  curriculumProgramId,
}) => {
  const isBehaviourModuleEnabledInModuleSettingsAndRnP = getIsBehaviourModuleEnabledInModuleSettingsAndRnP(
    curriculumProgramId
  );

  const context = [
    {
      entityId: curriculumProgramId,
      entityType: "CURRICULUM_PROGRAM",
    },
  ];

  return (
    isBehaviourModuleEnabledInModuleSettingsAndRnP &&
    (ACLStorage.getModuleSettingsValueByContext(
      STUDENT_PROFILE_CONTROL_PARAMETER.VIEW_BEHAVIOUR_TAB_FOR_ALL_STUDENTS_ACCOUNT,
      context
    ) ||
      ACLStorage.getPermissionValueByContext(
        STUDENT_PROFILE_CONTROL_PARAMETER.VIEW_BEHAVIOUR_TAB_FOR_ALL_STUDENTS_PROGRAMME,
        context
      ) ||
      (isStaffAssociatedWithStudent &&
        ACLStorage.getModuleSettingsValueByContext(
          STUDENT_PROFILE_CONTROL_PARAMETER.VIEW_BEHAVIOUR_TAB_FOR_THEIR_STUDENTS_PROGRAMME,
          context
        )))
  );
};

export const getCombinedAccessBlockerPropsForStudentProfile = ({
  userType,
  isStaffAssociatedWithStudent = false,
  curriculumProgramId,
}) => {
  const isBehaviourRnPModuleConsumptionEnabled = getIsBehaviourRnPModuleConsumptionEnabled();

  if (userType === STAFF && isBehaviourRnPModuleConsumptionEnabled) {
    const isStudentProfileModuleEnabledInRegularRnP = ACLStorage.getModuleSettingsValue(
      STUDENT_PROFILE_CONTROL_PARAMETER.MODULE_SETTING
    );

    const canAccessBehaviourTabInStudentProfile = getHasAccessToBehaviourTabFromBehaviourRnP(
      {
        isStaffAssociatedWithStudent,
        curriculumProgramId,
      }
    );

    return {
      isAccessBlocked:
        !isStudentProfileModuleEnabledInRegularRnP &&
        !canAccessBehaviourTabInStudentProfile,
    };
  }

  return { controlParameterObj: STUDENT_PORTFOLIO_CONTROL_PARAMETER };
};

export const PROFILE_ADDITIONAL_FIELDS = {
  firstAdditionalField: {
    displayPerm: "FeatureFlag:EnableFirstFieldInPersonalInfo",
    customLabelPerm: "StudentProfileFirstAdditionalFieldLabel",
  },
  secondAdditionalField: {
    displayPerm: "FeatureFlag:EnableSecondFieldInPersonalInfo",
    customLabelPerm: "StudentProfileSecondAdditionalFieldLabel",
  },
  thirdAdditionalField: {
    displayPerm: "FeatureFlag:EnableThirdFieldInPersonalInfo",
    customLabelPerm: "StudentProfileThirdAdditionalFieldLabel",
  },
  fourthAdditionalField: {
    displayPerm: "FeatureFlag:EnableFourthFieldInPersonalInfo",
    customLabelPerm: "StudentProfileFourthAdditionalFieldLabel",
  },
  fifthAdditionalField: {
    displayPerm: "FeatureFlag:EnableFifthFieldInPersonalInfo",
    customLabelPerm: "StudentProfileFifthAdditionalFieldLabel",
  },
  sixthAdditionalField: {
    displayPerm: "FeatureFlag:EnableSixthFieldInPersonalInfo",
    customLabelPerm: "StudentProfileSixthAdditionalFieldLabel",
  },
  seventhAdditionalField: {
    displayPerm: "FeatureFlag:EnableSeventhFieldInPersonalInfo",
    customLabelPerm: "StudentProfileSeventhAdditionalFieldLabel",
  },
  eighthAdditionalField: {
    displayPerm: "FeatureFlag:EnableEighthFieldInPersonalInfo",
    customLabelPerm: "StudentProfileEighthAdditionalFieldLabel",
  },
};

export const STUDENT_ADDITIONAL_FIELDS_TITLE_MAPPING = {
  firstAdditionalField: "firstNameLocale",
  secondAdditionalField: "lastNameLocale",
  thirdAdditionalField: "birthPlaceLocale",
  fourthAdditionalField: "dobLocale",
  fifthAdditionalField: "religionLocale",
  sixthAdditionalField: "primaryNationalityLocale",
  seventhAdditionalField: "seventhAdditionalField",
  eighthAdditionalField: "eighthAdditionalField",
};

export const userProfileColorPallet = PROFILE_AVATAR_COLORS;

export const OVERDUE = "OVERDUE";

export const DUE_BY = "DUE_BY";
export const DUE_DATE = "DUE_DATE";
export const ANNUAL_STATEMENT = "ANNUAL_STATEMENT";
export const CONTINGENCY = "CONTINGENCY";
export const OVERDUE_HEADER = "OVERDUE_HEADER";
export const DUE_BY_HEADER = "DUE_BY_HEADER";
export const ANNUAL_STATEMENT_HEADER = "ANNUAL_STATEMENT_HEADER";
export const CONTINGENCY_HEADER = "CONTINGENCY_HEADER";
export const OPENING_BALANCE = "OPENING_BALANCE";
export const SECURITY_DEPOSIT = "SECURITY_DEPOSIT";
export const PAST_DUES = "PAST_DUES";
export const PAYMENT_METHOD = "PAYMENT_METHOD";
export const ERROR_TYPE_MAPPING = {
  "TD/UPDUSR/001": "INVALID_UID",
  "TD/UPDUSR/002": "INVALID_EMAIL",
};
export const EMPTY_OBJECT = {};

export const getStudentProfileCustomFieldsConfig = () => {
  const node = getOrganizationConstantsFromCache({
    organizationId: getOrganizationId(),
    filter: { names: [STUDENT_PROFILE_CUSTOM_FIELDS_CONFIG] },
  });
  const customFieldsConfigString = _.get(node, "globalConstants.0.value");

  if (customFieldsConfigString) {
    try {
      return JSON.parse(customFieldsConfigString);
    } catch (e) {
      console.error("Failed to parse custom fields config:", e);
      return {};
    }
  } else {
    return {};
  }
};

export const isAdmin = () => getIsAdminByRole();

export const PROFILE_SECTION_NODES = {
  parent: {
    [CURRICULUM_TYPE_PYP]: [
      "overview",
      "studentFeed",
      "progressSummary",
      "reports",
      "goals",
      "attendance",
      "timetable",
      "otherInfo",
      "details",
      "studentFiles",
      "codeOfConduct",
      "billing",
      "behaviourRecord",
    ],
    [CURRICULUM_TYPE_MYP]: [
      "overview",
      "studentFeed",
      "progressSummary",
      "reports",
      "goals",
      "attendance",
      "timetable",
      "otherInfo",
      "details",
      "studentFiles",
      "codeOfConduct",
      "billing",
      "behaviourRecord",
    ],
    [CURRICULUM_TYPE_UBD]: [
      "overview",
      "studentFeed",
      "progressSummary",
      "reports",
      "goals",
      "attendance",
      "timetable",
      "otherInfo",
      "details",
      "studentFiles",
      "codeOfConduct",
      "billing",
      "behaviourRecord",
    ],
    [CURRICULUM_TYPE_DP]: [
      "overview",
      "studentFeed",
      "IBISRegistration",
      "ibEcoursework",
      "progressSummary",
      "reports",
      "attendance",
      "timetable",
      "otherInfo",
      "details",
      "goals",
      "studentFiles",
      "codeOfConduct",
      "billing",
      "behaviourRecord",
    ],
  },
  student: {
    [CURRICULUM_TYPE_PYP]: [
      "overview",
      "studentFeed",
      "progressSummary",
      "reports",
      "goals",
      "attendance",
      "timetable",
      "otherInfo",
      "details",
      "studentFiles",
      "codeOfConduct",
      "billing",
      "behaviourRecord",
    ],
    [CURRICULUM_TYPE_MYP]: [
      "overview",
      "studentFeed",
      "progressSummary",
      "reports",
      "goals",
      "attendance",
      "timetable",
      "otherInfo",
      "details",
      "studentFiles",
      "codeOfConduct",
      "billing",
      "behaviourRecord",
    ],
    [CURRICULUM_TYPE_UBD]: [
      "overview",
      "studentFeed",
      "progressSummary",
      "reports",
      "goals",
      "attendance",
      "timetable",
      "otherInfo",
      "details",
      "studentFiles",
      "codeOfConduct",
      "billing",
      "behaviourRecord",
    ],
    [CURRICULUM_TYPE_DP]: [
      "overview",
      "studentFeed",
      "IBISRegistration",
      "ibEcoursework",
      "progressSummary",
      "reports",
      "attendance",
      "timetable",
      "otherInfo",
      "details",
      "goals",
      "studentFiles",
      "codeOfConduct",
      "billing",
      "behaviourRecord",
    ],
  },
  staff: {
    [CURRICULUM_TYPE_PYP]: [
      "overview",
      "studentFeed",
      "progressSummary",
      "reports",
      "goals",
      "attendance",
      "timetable",
      "studentAlerts",
      "calendar",
      "teacher_notes",
      "otherInfo",
      "details",
      "studentFiles",
      "codeOfConduct",
      "billing",
      "behaviourRecord",
    ],
    [CURRICULUM_TYPE_MYP]: [
      "overview",
      "studentFeed",
      "progressSummary",
      "reports",
      "goals",
      "attendance",
      "timetable",
      "studentAlerts",
      "calendar",
      "teacher_notes",
      "otherInfo",
      "details",
      "studentFiles",
      "codeOfConduct",
      "billing",
      "behaviourRecord",
    ],
    [CURRICULUM_TYPE_UBD]: [
      "overview",
      "studentFeed",
      "progressSummary",
      "reports",
      "goals",
      "attendance",
      "timetable",
      "studentAlerts",
      "calendar",
      "teacher_notes",
      "otherInfo",
      "details",
      "studentFiles",
      "codeOfConduct",
      "billing",
      "behaviourRecord",
    ],
    [CURRICULUM_TYPE_DP]: [
      "overview",
      "studentFeed",
      "ibEcoursework",
      "progressSummary",
      "reports",
      "attendance",
      "timetable",
      "studentAlerts",
      "calendar",
      "goals",
      "teacher_notes",
      "otherInfo",
      "details",
      "studentFiles",
      "codeOfConduct",
      "billing",
      "behaviourRecord",
    ],
  },
};

/**
 * Student profile page tabs
 */
export const typesConfig = {
  overview: {
    value: "overview",
    label: "common:overview",
    otherCondition: args => {
      /* If no widgets are visible in overview, hide the tab */

      const {
        userType,
        curriculumProgram,
        curriculumProgramId,
        projectGroupCategoryIdByType,
      } = args;

      const leftWidgets = _.get(
        WIDGET_TYPES_NODES_LEFT,
        [userType, curriculumProgram],
        []
      );
      const rightWidgets = _.get(
        WIDGET_TYPES_NODES_RIGHT,
        [userType, curriculumProgram],
        []
      );
      const allWidgets = [...leftWidgets, ...rightWidgets];

      return _.some(allWidgets, widgetType =>
        isWidgetVisible({
          userType,
          type: widgetType,
          projectGroupCategoryIdByType,
          currentCurriculumProgramId: curriculumProgramId,
          currentCurriculumProgramType: curriculumProgram,
        })
      );
    },
    /*  Permissions are not explicitly defined for this tab as 
        they are checked on widget level for the overview tab. */
  },
  IBISRegistration: {
    value: "IBISRegistration",
    label: "common:ibis_registration",
    otherCondition: ({ showIBTab }) => showIBTab, //if we want to render data based on some condition
  },
  studentFeed: {
    value: "studentFeed",
    label: "common:portfolio",
    controlParameters: {
      permissions: [STUDENT_PROFILE_CONTROL_PARAMETER.VIEW_PORTFOLIO_POST],
    },
  },
  progressSummary: {
    value: "progressSummary",
    label: "common:gradebook",
    perms: {
      staff: STUDENT_PROGRESS_SUMMARY_PERMISSION_MAPPING,
      student: STUDENT_PROGRESS_SUMMARY_PERMISSION_MAPPING,
      parent: STUDENT_PROGRESS_SUMMARY_PERMISSION_MAPPING,
    },
    controlParameters: {
      // TODO: Add module setting
      permissions: [
        STUDENT_PROFILE_CONTROL_PARAMETER.VIEW_ASSIGNMENTS_AND_GRADEBOOK,
      ],
    },
  },
  goals: {
    value: "goals",
    label: "teacherHomePage:goals",
    controlParameters: {
      permissions: [STUDENT_PROFILE_CONTROL_PARAMETER.VIEW_GOALS],
      moduleSettings: [CAN_VIEW_STUDENT_GOALS],
    },
  },
  reports: {
    value: "reports",
    label: "common:progress_reports",
    perms: {
      staff: {
        [CURRICULUM_TYPE_PYP]: ["Common:EnableReportsInStudentProfile"],
        [CURRICULUM_TYPE_MYP]: ["Common:EnableReportsInStudentProfile"],
        [CURRICULUM_TYPE_UBD]: ["Common:EnableReportsInStudentProfile"],
        [CURRICULUM_TYPE_DP]: ["Common:EnableReportsInStudentProfile"],
      },
      student: {
        [CURRICULUM_TYPE_PYP]: ["Common:EnableReportsInStudentProfile"],
        [CURRICULUM_TYPE_MYP]: ["Common:EnableReportsInStudentProfile"],
        [CURRICULUM_TYPE_UBD]: ["Common:EnableReportsInStudentProfile"],
        [CURRICULUM_TYPE_DP]: ["Common:EnableReportsInStudentProfile"],
      },
      parent: {
        [CURRICULUM_TYPE_PYP]: ["Common:EnableReportsInStudentProfile"],
        [CURRICULUM_TYPE_MYP]: ["Common:EnableReportsInStudentProfile"],
        [CURRICULUM_TYPE_UBD]: ["Common:EnableReportsInStudentProfile"],
        [CURRICULUM_TYPE_DP]: ["Common:EnableReportsInStudentProfile"],
      },
    },
    controlParameters: {
      permissions: [STUDENT_PROFILE_CONTROL_PARAMETER.VIEW_PROGRESS_REPORTS],
    },
  },
  attendance: {
    value: "attendance",
    label: "common:attendance",
    otherCondition: ({ attendanceRecordType }) =>
      !_.isNull(attendanceRecordType),
    controlParameters: {
      permissions: [
        STUDENT_PROFILE_CONTROL_PARAMETER.VIEW_ATTENDANCE_AND_HOMEROOM,
        STUDENT_PROFILE_CONTROL_PARAMETER.MANAGE_EXCUSALS,
      ],
    },
  },
  timetable: {
    value: "timetable",
    label: "common:timetable",
    controlParameters: {
      permissions: [STUDENT_PROFILE_CONTROL_PARAMETER.VIEW_TIMETABLE],
    },
    perms: {
      staff: {
        [CURRICULUM_TYPE_PYP]: [ENABLE_STUDENT_TIMETABLE_PERMISSION],
        [CURRICULUM_TYPE_MYP]: [ENABLE_STUDENT_TIMETABLE_PERMISSION],
        [CURRICULUM_TYPE_UBD]: [ENABLE_STUDENT_TIMETABLE_PERMISSION],
        [CURRICULUM_TYPE_DP]: [ENABLE_STUDENT_TIMETABLE_PERMISSION],
      },
      student: {
        [CURRICULUM_TYPE_PYP]: [
          "Common:EnableStudentTimeTableForStudentPYP",
          ENABLE_STUDENT_TIMETABLE_PERMISSION,
        ],
        [CURRICULUM_TYPE_MYP]: [
          "Common:EnableStudentTimeTableForStudentMYP",
          ENABLE_STUDENT_TIMETABLE_PERMISSION,
        ],
        [CURRICULUM_TYPE_UBD]: [
          "Common:EnableStudentTimeTableForStudentUBD",
          ENABLE_STUDENT_TIMETABLE_PERMISSION,
        ],
        [CURRICULUM_TYPE_DP]: [
          "Common:EnableStudentTimeTableForStudentDP",
          ENABLE_STUDENT_TIMETABLE_PERMISSION,
        ],
      },
      parent: {
        [CURRICULUM_TYPE_PYP]: [
          "Common:EnableStudentTimeTableForParentPYP",
          ENABLE_STUDENT_TIMETABLE_PERMISSION,
        ],
        [CURRICULUM_TYPE_MYP]: [
          "Common:EnableStudentTimeTableForParentMYP",
          ENABLE_STUDENT_TIMETABLE_PERMISSION,
        ],
        [CURRICULUM_TYPE_UBD]: [
          "Common:EnableStudentTimeTableForParentUBD",
          ENABLE_STUDENT_TIMETABLE_PERMISSION,
        ],
        [CURRICULUM_TYPE_DP]: [
          "Common:EnableStudentTimeTableForParentDP",
          ENABLE_STUDENT_TIMETABLE_PERMISSION,
        ],
      },
    },
  },
  studentAlerts: {
    value: "student-alerts",
    label: "common:student_alert_plural",
    controlParameters: {
      permissions: [STUDENT_PROFILE_CONTROL_PARAMETER.VIEW_STUDENT_FLAGS],
    },
  },
  teacher_notes: {
    value: "teacher_notes",
    label: "common:teacher_notes",
    controlParameters: {
      permissions: [STUDENT_PROFILE_CONTROL_PARAMETER.VIEW_TEACHER_NOTES],
    },
  },
  otherInfo: {
    value: "otherInfo",
    label: "common:other_info",
    controlParameters: {
      permissions: [STUDENT_PROFILE_CONTROL_PARAMETER.VIEW_OTHER_INFO],
    },
  },
  details: {
    value: "details",
    label: "common:details",
    // TODO: Remove openapply check once we have move all openapply orgs to showDetailsTabForIntegrations
    otherCondition: ({
      syncServices,
      showDetailsTabForNonSyncOrg,
      showDetailsTabForIntegrations,
    }) =>
      showDetailsTabForNonSyncOrg ||
      showDetailsTabForIntegrations ||
      (!_.isEmpty(syncServices) && _.first(syncServices) === "OPENAPPLY"),
    perms: {
      staff: {
        [CURRICULUM_TYPE_PYP]: ["TeacherPortal:StudentProfileDetails"],
        [CURRICULUM_TYPE_MYP]: ["TeacherPortal:StudentProfileDetails"],
        [CURRICULUM_TYPE_UBD]: ["TeacherPortal:StudentProfileDetails"],
        [CURRICULUM_TYPE_DP]: ["TeacherPortal:StudentProfileDetails"],
      },
      student: {
        [CURRICULUM_TYPE_PYP]: ["StudentPortal:StudentProfileDetailsPYP"],
        [CURRICULUM_TYPE_MYP]: ["StudentPortal:StudentProfileDetailsMYP"],
        [CURRICULUM_TYPE_UBD]: ["StudentPortal:StudentProfileDetailsUBD"],
        [CURRICULUM_TYPE_DP]: ["StudentPortal:StudentProfileDetailsDP"],
      },
      parent: {
        [CURRICULUM_TYPE_PYP]: ["FamilyPortal:StudentProfileDetailsPYP"],
        [CURRICULUM_TYPE_MYP]: ["FamilyPortal:StudentProfileDetailsMYP"],
        [CURRICULUM_TYPE_UBD]: ["FamilyPortal:StudentProfileDetailsUBD"],
        [CURRICULUM_TYPE_DP]: ["FamilyPortal:StudentProfileDetailsDP"],
      },
    },
  },
  studentFiles: {
    value: "studentFiles",
    label: "common:student_files",
    controlParameters: {
      permissions: [
        STUDENT_PROFILE_CONTROL_PARAMETER.VIEW_AND_UPDATE_STUDENT_FILES,
      ],
    },
  },
  codeOfConduct: {
    value: "codeOfConduct",
    label: "codeofconduct:code_of_conduct",
    perms: {
      staff: {
        [CURRICULUM_TYPE_PYP]: ["Common:CodeOfConduct"],
        [CURRICULUM_TYPE_MYP]: ["Common:CodeOfConduct"],
        [CURRICULUM_TYPE_UBD]: ["Common:CodeOfConduct"],
        [CURRICULUM_TYPE_DP]: ["Common:CodeOfConduct"],
      },
      student: {
        [CURRICULUM_TYPE_PYP]: ["Common:CodeOfConduct"],
        [CURRICULUM_TYPE_MYP]: ["Common:CodeOfConduct"],
        [CURRICULUM_TYPE_UBD]: ["Common:CodeOfConduct"],
        [CURRICULUM_TYPE_DP]: ["Common:CodeOfConduct"],
      },
      parent: {
        [CURRICULUM_TYPE_PYP]: ["Common:CodeOfConduct"],
        [CURRICULUM_TYPE_MYP]: ["Common:CodeOfConduct"],
        [CURRICULUM_TYPE_UBD]: ["Common:CodeOfConduct"],
        [CURRICULUM_TYPE_DP]: ["Common:CodeOfConduct"],
      },
    },
  },
  calendar: {
    value: "calendar",
    label: "common:calendar",
    perms: {
      staff: {
        [CURRICULUM_TYPE_PYP]: ["FeatureFlag:StudentProfileCalendar"],
        [CURRICULUM_TYPE_MYP]: ["FeatureFlag:StudentProfileCalendar"],
        [CURRICULUM_TYPE_UBD]: ["FeatureFlag:StudentProfileCalendar"],
        [CURRICULUM_TYPE_DP]: ["FeatureFlag:StudentProfileCalendar"],
      },
    },
    controlParameters: {
      permissions: [STUDENT_PROFILE_CONTROL_PARAMETER.VIEW_CALENDAR],
    },
  },
  billing: {
    value: "billing",
    label: "common:billing",
    perms: {
      staff: {
        [CURRICULUM_TYPE_PYP]: ["TeacherPortal:StudentBillingInfo"],
        [CURRICULUM_TYPE_MYP]: ["TeacherPortal:StudentBillingInfo"],
        [CURRICULUM_TYPE_UBD]: ["TeacherPortal:StudentBillingInfo"],
        [CURRICULUM_TYPE_DP]: ["TeacherPortal:StudentBillingInfo"],
      },
      student: {
        [CURRICULUM_TYPE_PYP]: ["StudentPortal:StudentBillingInfo"],
        [CURRICULUM_TYPE_MYP]: ["StudentPortal:StudentBillingInfo"],
        [CURRICULUM_TYPE_UBD]: ["StudentPortal:StudentBillingInfo"],
        [CURRICULUM_TYPE_DP]: ["StudentPortal:StudentBillingInfo"],
      },
      parent: {
        [CURRICULUM_TYPE_PYP]: ["FamilyPortal:StudentBillingInfo"],
        [CURRICULUM_TYPE_MYP]: ["FamilyPortal:StudentBillingInfo"],
        [CURRICULUM_TYPE_UBD]: ["FamilyPortal:StudentBillingInfo"],
        [CURRICULUM_TYPE_DP]: ["FamilyPortal:StudentBillingInfo"],
      },
    },
  },
  ibEcoursework: {
    value: "ibEcoursework",
    label: "teacherHomePage:ib_ecoursework",
    controlParameters: {
      permissions: [
        STUDENT_PROFILE_CONTROL_PARAMETER.VIEW_PROJECTS_AND_COURSEWORK,
      ],
      moduleSettings: [ENABLE_IBECOURSEWORK],
    },
  },
  behaviourRecord: {
    value: "behaviourRecord",
    label: "behaviour:behaviour_management",
    perms: {
      staff: {
        [CURRICULUM_TYPE_PYP]: ["FeatureFlag:BehaviourManagement"],
        [CURRICULUM_TYPE_MYP]: ["FeatureFlag:BehaviourManagement"],
        [CURRICULUM_TYPE_UBD]: ["FeatureFlag:BehaviourManagement"],
        [CURRICULUM_TYPE_DP]: ["FeatureFlag:BehaviourManagement"],
      },
      student: {
        [CURRICULUM_TYPE_PYP]: ["FeatureFlag:BehaviourManagement"],
        [CURRICULUM_TYPE_MYP]: ["FeatureFlag:BehaviourManagement"],
        [CURRICULUM_TYPE_UBD]: ["FeatureFlag:BehaviourManagement"],
        [CURRICULUM_TYPE_DP]: ["FeatureFlag:BehaviourManagement"],
      },
      parent: {
        [CURRICULUM_TYPE_PYP]: ["FeatureFlag:BehaviourManagement"],
        [CURRICULUM_TYPE_MYP]: ["FeatureFlag:BehaviourManagement"],
        [CURRICULUM_TYPE_UBD]: ["FeatureFlag:BehaviourManagement"],
        [CURRICULUM_TYPE_DP]: ["FeatureFlag:BehaviourManagement"],
      },
    },
    otherCondition: ({
      userType,
      isStaffAssociatedWithStudent,
      curriculumProgramId,
    }) => {
      const isBehaviourRnPModuleConsumptionEnabled = getIsBehaviourRnPModuleConsumptionEnabled();
      if (userType === STAFF && isBehaviourRnPModuleConsumptionEnabled) {
        return getHasAccessToBehaviourTabFromBehaviourRnP({
          isStaffAssociatedWithStudent,
          curriculumProgramId,
        });
      }

      // show behaviour tab in profile only when behaviour module is enabled
      return (
        ACLStorage.getModuleSettingsValue(
          BEHAVIOUR_DASHBOARD_ACCESS_PERMISSION
        ) &&
        ACLStorage.getPermissionValue(
          STUDENT_PROFILE_CONTROL_PARAMETER.VIEW_BEHAVIOUR_INCIDENTS
        )
      );
    },
  },
};

export const getBillingPaymentDetailsTableColumns = () => {
  return [
    {
      key: "date",
      locale: "common:date",
      id: "ACCOUNTING_DT",
      type: "TEXTCOLUMN",
      style: {
        width: "160px",
      },
    },
    {
      key: "payment_method",
      locale: "common:payment_method",
      id: "PAYMENT_METHOD",
      type: "TEXTCOLUMN",
      style: {
        width: "364px",
      },
    },
    {
      key: "amount",
      locale: "common:amount",
      id: "INDIAN_PAYMENT",
      type: "TEXTCOLUMN",
    },
  ];
};

export const getBillingTableColumns = ({ filter }) => {
  const isContingencyTab = filter === CONTINGENCY;
  const isAnnualStatementTab = filter === ANNUAL_STATEMENT;

  if (isContingencyTab) {
    return [
      {
        key: "date",
        locale: "common:date",
        id: "PW_DATE",
        type: "TEXTCOLUMN",
      },
      {
        locale: "common:particular_plural",
        id: "PARTICULARS",
        key: "particular",
        type: "CUSTOM",
      },
      {
        locale: "common:charges",
        id: "COST",
        key: "charges",
        type: "TEXTCOLUMN",
      },
      {
        key: "deposits",
        locale: "common:deposits",
        id: "DEPOSIT",
        type: "TEXTCOLUMN",
      },
      {
        locale: "common:balance",
        id: "BALANCE",
        key: "balance",
        type: "TEXTCOLUMN",
      },
    ];
  }

  return [
    {
      key: "PW_DUE_DT",
      locale: "common:due_date",
      id: "PW_DUE_DT",
      type: "TEXTCOLUMN",
      style: {
        width: isAnnualStatementTab ? "160px" : "auto",
      },
    },
    ...(!isAnnualStatementTab
      ? [
          {
            locale: "common:installment",
            id: "PW_INSTALLMENT",
            key: "installment",
            type: "TEXTCOLUMN",
          },
        ]
      : []),
    {
      locale: "common:particular",
      id: "DESCR_LN1",
      key: "particular",
      type: "TEXTCOLUMN",
      style: {
        width: isAnnualStatementTab ? "364px" : "auto",
      },
    },
    {
      locale: "common:charges",
      id: "BILLING_AMT",
      key: "charges",
      type: "TEXTCOLUMN",
      style: {
        width: isAnnualStatementTab ? "200px" : "auto",
      },
    },
    {
      locale: "common:paid_adjustments",
      id: "PAY_CR",
      key: "paid",
      type: "TEXTCOLUMN",
      style: {
        width: isAnnualStatementTab ? "200px" : "auto",
      },
    },
    {
      locale: "common:balance",
      id: "BALANCE",
      key: "balance",
      type: "TEXTCOLUMN",
    },
  ];
};

export const formatNumberToIndianRupees = amt => {
  if (amt || amt === 0) {
    return `₹ ${amt?.toLocaleString("en-IN")}`;
  }
};

const currentYear = moment().year();

export const getNewYearStart = (inputFormat = "DD/MM/YYYY") => {
  return moment().startOf("year").format(inputFormat);
};

export const getFinancialYearStart = (inputFormat = "DD/MM/YYYY") => {
  return moment({
    year: currentYear,
    month: 3,
    day: 1,
  }).format(inputFormat);
};

const PAYMENT_METHODS = {
  CC: "common:credit_card",
  CHK: "common:cheque",
  CSH: "common:cash",
  GE: "common:cash",
  DD: "common:direct_debit",
  DR: "common:draft",
  EC: "common:electronic_cheque",
  EFT: "common:electronic_fund_transfer",
  PAYPAL: "common:paypal",
};

export const getPaymentMethodLabel = ({ t, method }) => {
  const locale = _.get(PAYMENT_METHODS, method);

  return locale ? t(locale) : method;
};

export const getTodosPermission = (oldPerm, isStaff = false) => {
  return isStaff ? [] : ["Common:StudentTodos"];
};

const getBillingInfoFilters = params => {
  const {
    t,
    studentBillingInfo,
    redirectUrl,
    loadingState,
    openLinkInNewTab,
  } = params;

  const isRedirectButtonEnabled = ACLStore.can(
    "EnablePaymentButtonInStudentBilling"
  );

  return [
    {
      key: OVERDUE,
      count: t("common:overdue"),
      value: OVERDUE,
      label: _.get(studentBillingInfo, "overdue", 0),
      color: _.get(studentBillingInfo, "overdue", 0) > 0 && colors.textCritical,
      isLabelLoading: loadingState?.OVERDUE_HEADER,
      testId: "overdue",
      buttonLabel: t("common:pay_now"),
      showButtonWithLabel: isRedirectButtonEnabled && redirectUrl,
      onButtonClick: () => openLinkInNewTab({ url: redirectUrl }),
    },
    {
      key: DUE_BY,
      count: t("common:due_by_date", {
        date: _.get(studentBillingInfo, "dueDate", ""),
      }),
      value: DUE_BY,
      label: _.get(studentBillingInfo, "due", 0),
      isLabelLoading: loadingState?.DUE_BY_HEADER,
      testId: "dueBy",
      buttonLabel: t("common:pay_now"),
      showButtonWithLabel: isRedirectButtonEnabled && redirectUrl,
      onButtonClick: () => openLinkInNewTab({ url: redirectUrl }),
    },
    {
      key: ANNUAL_STATEMENT,
      count: t("common:annual_statement"),
      value: ANNUAL_STATEMENT,
      label: _.get(studentBillingInfo, "annualStatement", 0),
      isLabelLoading: loadingState?.ANNUAL_STATEMENT_HEADER,
    },
    {
      key: CONTINGENCY,
      count: t("common:contingency"),
      value: CONTINGENCY,
      label: _.get(studentBillingInfo, "contingency", 0),
      color:
        _.get(studentBillingInfo, "contingency", 0) < 0 && colors.textCritical,
      isLabelLoading:
        loadingState?.CONTINGENCY_HEADER || loadingState?.OPENING_BALANCE,
    },
    {
      key: SECURITY_DEPOSIT,
      count: t("common:security_deposit"),
      value: SECURITY_DEPOSIT,
      label: _.get(studentBillingInfo, "securityDeposit", 0),
      isClickable: false,
      color:
        _.get(studentBillingInfo, "securityDeposit", 0) < 0 &&
        colors.textCritical,
      isLabelLoading: loadingState?.SECURITY_DEPOSIT_HEADER,
    },
  ];
};

export const getBillingInfoFiltersMemoized = getMemoizedFunction(
  getBillingInfoFilters
);

/**
 * Overview page widgets
 * all constants for this page is below
 */
export const widgetTypesConfig = {
  PERSONAL_INFORMATION: {
    id: "PERSONAL_INFORMATION",
    value: "common:personal_info",
    editType: "EDIT",
    isEdit: true,
    studentEditPerm: "FeatureFlag:EnablePersonalInfoStudentEdit",
    editTitle: "common:edit",
    icon: PencilLineOutlined,
    controlParameters: {
      permissions: [
        STUDENT_PROFILE_CONTROL_PARAMETER.VIEW_BASIC_DETAILS,
        STUDENT_PROFILE_CONTROL_PARAMETER.UPDATE_DETAILS,
      ],
    },
    level: 2,
  },
  BILLING: {
    id: "BILLING",
    value: "common:personal_info",
    isEdit: false,
    level: 2,
  },
  HOMEROOMWIDGET: {
    id: "HOMEROOMWIDGET",
    value: "attendance:homeroom_advisor",
    editType: "EDIT",
    isEdit: true,
    icon: PencilLineOutlined,
    level: 2,
    isStudentEdit: false,
    isParentEdit: false,
    controlParameters: {
      permissions: [
        STUDENT_PROFILE_CONTROL_PARAMETER.VIEW_ATTENDANCE_AND_HOMEROOM,
        STUDENT_PROFILE_CONTROL_PARAMETER.UPDATE_HOMEROOM_AND_CLASSES,
      ],
    },
    curriculumPrograms: ["IB_PYP", "IB_MYP", "UBD", "IB_DP"],
  },
  ATTENDANCE: {
    id: "ATTENDANCE",
    value: "common:attendance",
    editType: "PATH",
    isEdit: true,
    isStudentEdit: true,
    isParentEdit: true,
    icon: ExpandArrowsTwoOutlined,
    level: 2,
    controlParameters: {
      permissions: [
        STUDENT_PROFILE_CONTROL_PARAMETER.VIEW_ATTENDANCE_AND_HOMEROOM,
      ],
    },
    curriculumPrograms: ["IB_PYP", "IB_MYP", "UBD", "IB_DP"],
  },
  CONTACT_INFORMATION: {
    id: "CONTACT_INFORMATION",
    value: "common:contact_information",
    editType: "EDIT",
    editTitle: "common:add_contact_details",
    isEdit: true,
    icon: AddCircleOutlined,
    level: 2,
    perms: ["FeatureFlag:EnableContactInformation"],
    controlParameters: {
      permissions: [
        STUDENT_PROFILE_CONTROL_PARAMETER.VIEW_BASIC_DETAILS,
        STUDENT_PROFILE_CONTROL_PARAMETER.UPDATE_DETAILS,
      ],
    },
  },
  FAMILY_INFORMATION: {
    id: "FAMILY_INFORMATION",
    value: "common:family_information",
    editType: "EDIT",
    editTitle: "common:invite_family_members",
    isEdit: true,
    icon: AddCircleOutlined,
    level: 2,
    curriculumPrograms: ["IB_PYP", "IB_MYP", "UBD", "IB_DP"],
    controlParameters: {
      permissions: [
        STUDENT_PROFILE_CONTROL_PARAMETER.VIEW_BASIC_DETAILS,
        STUDENT_PROFILE_CONTROL_PARAMETER.UPDATE_DETAILS,
      ],
    },
    perms: [DISABLE_FAMILY_INVITE_OPTIONS],
  },
  SUBMISSION_INSIGHTS: {
    id: "SUBMISSION_INSIGHTS",
    value: "classRoom:submission_insights_all_classes",
    editType: "PATH",
    editTitle: "common:see_all",
    isEdit: true,
    isStudentEdit: true,
    isParentEdit: true,
    icon: EyeOutlined,
    level: 2,
    controlParameters: {
      permissions: [
        STUDENT_PROFILE_CONTROL_PARAMETER.VIEW_ASSIGNMENTS_AND_GRADEBOOK,
      ],
    },
    curriculumPrograms: ["IB_PYP", "IB_MYP", "UBD", "IB_DP"],
  },
  PERSONAL_PROJECT: {
    id: "PERSONAL_PROJECT",
    value: "project:personal_project",
    editType: "PATH",
    isEdit: true,
    icon: ExpandArrowsTwoOutlined,
    level: 2,
    curriculumPrograms: ["IB_MYP"],
    controlParameters: {
      permissions: [
        STUDENT_PROFILE_CONTROL_PARAMETER.VIEW_PROJECTS_AND_COURSEWORK,
      ],
      // passed to get contextId for projectGroup
      projectGroupType: PROJECT_GROUP_TYPE_PERSONAL,
      moduleSettings: [CAN_VIEW_AND_MANAGE_STUDENT_PROJECTS],
    },
  },
  COMMUNITY_PROJECT: {
    id: "COMMUNITY_PROJECT",
    value: "project:community_project",
    editType: "PATH",
    isEdit: true,
    icon: ExpandArrowsTwoOutlined,
    curriculumPrograms: ["IB_MYP"],
    level: 2,
    controlParameters: {
      permissions: [
        STUDENT_PROFILE_CONTROL_PARAMETER.VIEW_PROJECTS_AND_COURSEWORK,
      ],
      // passed to get contextId for projectGroup
      projectGroupType: PROJECT_GROUP_TYPE_COMMUNITY,
      moduleSettings: [CAN_VIEW_AND_MANAGE_STUDENT_PROJECTS],
    },
  },
  TODO: {
    id: "TODO",
    value: "classRoom:todo",
    editType: "PATH",
    isEdit: true,
    editTitle: "common:see_all",
    icon: EyeOutlined,
    level: 2,
    controlParameters: {
      // TODO: Add module settings
      permissions: [
        STUDENT_PROFILE_CONTROL_PARAMETER.VIEW_ASSIGNMENTS_AND_GRADEBOOK,
      ],
    },
    otherCondition: ({ userType }) => {
      let perms = [];

      if (userType === "staff") {
        perms = getTodosPermission(["Common:Classroom"], true);
      } else {
        perms = getTodosPermission([
          "Common:ShowConsolidatedDeadlines",
          "Common:Classroom",
        ]);
      }

      return ACLStore.can(perms, "all");
    },
  },
  PROJECT: {
    id: "PROJECT",
    value: "classRoom:todo",
    editType: "PATH",
    isEdit: true,
    editTitle: "common:see_all",
    icon: EyeOutlined,
    level: 2,
    perms: [],
  },
  SERVICE_AS_ACTION: {
    id: "SERVICE_AS_ACTION",
    value: "project:service_as_action",
    editType: "PATH",
    isEdit: true,
    icon: ExpandArrowsTwoOutlined,
    curriculumPrograms: ["IB_MYP"],
    level: 2,
    controlParameters: {
      permissions: [
        STUDENT_PROFILE_CONTROL_PARAMETER.VIEW_PROJECTS_AND_COURSEWORK,
      ],
      // passed to get contextId for projectGroup
      projectGroupType: PROJECT_GROUP_TYPE_SERVICE_AS_ACTON,
      moduleSettings: [CAN_VIEW_AND_MANAGE_STUDENT_PROJECTS],
    },
  },
  REPORT_OVERVIEW: {
    id: "REPORT_OVERVIEW",
    value: "studentProgressSummary:progress_report_overview",
    level: 3,
    perms: [],
    curriculumPrograms: ["IB_MYP"],
  },
  atls: {
    id: "atls",
    value: "common:atls_label",
    level: 3,
    perms: [],
    curriculumPrograms: ["IB_PYP", "IB_DP", "UBD"],
    curriculumLabels: {
      UBD: "common:ubd_skills",
    },
  },
  MYP_CRITERIA: {
    id: "MYP_CRITERIA",
    value: "studentProgressSummary:myp_criteria",
    level: 3,
    perms: [],
    curriculumPrograms: ["IB_MYP"],
  },
  SLO: {
    id: "SLO",
    value: "project:service_learning_outcomes",
    level: 3,
    perms: [],
    curriculumPrograms: ["IB_MYP"],
  },
  CAS: {
    id: "CAS",
    value: "project:creativity_activity_service_cas",
    editType: "PATH",
    isEdit: true,
    icon: ExpandArrowsTwoOutlined,
    level: 2,
    perms: ["Common:CASProjects"],
  },
  lp: {
    id: "lp",
    value: "common:lp_label",
    level: 3,
    perms: [],
    curriculumPrograms: ["IB_PYP", "IB_DP"],
  },
  concepts: {
    id: "concepts",
    value: "common:concept_plural",
    level: 3,
    perms: [],
    curriculumPrograms: ["IB_PYP"],
  },
  benchmarks: {
    id: "benchmarks",
    value: "common:content_standards",
    level: 3,
    perms: [],
    curriculumPrograms: ["IB_PYP", "IB_DP", "UBD"],
  },
  IB_CANDIDATE_INFORMATION: {
    id: "IB_CANDIDATE_INFORMATION",
    value: "common:candidate_information",
    editType: "EDIT",
    isEdit: true,
    approvalLabel: "common:candidate_information",
    approvalConfig: {
      label: "common:candidate_information",
      statusKey: "basicDetailsApprovalStatus",
      approvalKey: "isBasicDetailsApproved",
      approvedAtKey: "basicDetailsApprovedAt",
      approvedByKey: "basicDetailsApprovedBy",
      apiKey: "isBasicDetailsApproved",
      isApprovalSentKey: "isBasicDetailsApprovalNotificationSent",
      approvalSentByKey: "basicDetailsApprovalNotificationSentBy",
    },
    level: 2,
  },
  IB_EXAM_DETAILS: {
    id: "IB_EXAM_DETAILS",
    value: "common:exam_details",
    level: 2,
  },
  IB_SUBJECTS: {
    id: "IB_SUBJECTS",
    value: "common:subject_plural",
    editType: "EDIT",
    approvalConfig: {
      label: "configuration:dp_subjects",
      statusKey: "subjectsApprovalStatus",
      approvalKey: "isSubjectsApproved",
      approvedAtKey: "subjectsApprovedAt",
      approvedByKey: "subjectsApprovedBy",
      apiKey: "isSubjectsApproved",
      isApprovalSentKey: "isSubjectsApprovalNotificationSent",
      approvalSentByKey: "subjectsApprovalNotificationSentBy",
    },
    isEdit: true,
    level: 2,
  },
  IB_ECOURSEWORK_OVERVIEW: {
    id: "IB_ECOURSEWORK_OVERVIEW",
    value: "teacherHomePage:ib_ecoursework",
    editTitle: "community:see_all",
    isEdit: true,
    isStudentEdit: true,
    isParentEdit: true,
    editType: "PATH",
    icon: EyeOutlined,
    level: 2,
    curriculumPrograms: ["IB_DP"],
    controlParameters: {
      permissions: [
        STUDENT_PROFILE_CONTROL_PARAMETER.VIEW_PROJECTS_AND_COURSEWORK,
      ],
      moduleSettings: [ENABLE_IBECOURSEWORK],
    },
  },
  DP_OBJECTIVES: {
    id: "DP_OBJECTIVES",
    value: "common:DP_OBJECTIVES_label",
    level: 3,
    perms: [],
    curriculumPrograms: ["IB_DP"],
  },
  CLASSES: {
    id: "CLASSES",
    value: "common:class_plural",
    editTitle: "schoolSetup:add_to_classes",
    isEdit: true,
    editType: "EDIT",
    icon: AddCircleOutlined,
    level: 2,
    controlParameters: {
      permissions: [
        STUDENT_PROFILE_CONTROL_PARAMETER.VIEW_BASIC_DETAILS,
        STUDENT_PROFILE_CONTROL_PARAMETER.UPDATE_HOMEROOM_AND_CLASSES,
      ],
    },
    curriculumPrograms: [
      CURRICULUM_TYPE_PYP,
      CURRICULUM_TYPE_MYP,
      CURRICULUM_TYPE_UBD,
      CURRICULUM_TYPE_DP,
    ],
  },
  THIRD_PARTY_ACCOUNT: {
    id: "THIRD_PARTY_ACCOUNT",
    value: "common:account_plural",
    level: 2,
    curriculumPrograms: [
      CURRICULUM_TYPE_PYP,
      CURRICULUM_TYPE_MYP,
      CURRICULUM_TYPE_UBD,
      CURRICULUM_TYPE_DP,
    ],
  },
};

/**
 * this is family invitation status constants
 */

export const familyInvitationConstants = {
  ["INVITED"]: {
    label: "common:invite_sent",
    color: colors.decorativeSurfaceTwoD,
    backgroundColor: colors.decorativeSurfaceTwoL,
  },
  ["ROSTERED"]: {
    label: "common:connected",
    color: colors.decorativeSurfaceFiveD,
    backgroundColor: colors.decorativeSurfaceFiveL,
  },
  ["REQUESTED"]: {
    label: "common:approval_requested",
    color: colors.yellow400,
    backgroundColor: colors.decorativeSurfaceFiveL,
  },
};

export const familyInvitationConstantsV2 = {
  ["INVITED"]: {
    label: "common:invite_sent",
    color: colors.textSubtle,
  },
  ["ROSTERED"]: {
    label: "common:connected",
    color: colors.textSuccess,
  },
  ["REQUESTED"]: {
    label: "common:approval_requested",
    color: colors.textWarning,
  },
};

export const familyInvitationConstantV2 = {
  ["INVITED"]: {
    label: "common:invite_sent",
    color: "violet",
  },
  ["ROSTERED"]: {
    label: "common:connected",
    color: "teal",
  },
  ["REQUESTED"]: {
    label: "common:approval_requested",
    color: "yellow",
  },
};

/**
 * date format for this module
 */

export const dateFormat = {
  default: "DD MMM, YYYY",
  dmyFormat: "DD MMM, YYYY",
  ymdFormat: "YYYY MM DD",
  userBirthDateFormat: "DD MMM YYYY",
  projectDeadLineFormat: "Do MMMM",
  dateSelectorFormat: "YYYY/MM/DD",
  dobValueFormat: "YYYY MM DD",
  enrollmentDateFormat: DATE_TIME_CONSTANTS.DD_MMM_YYYY,
};

export const WIDGET_TYPES_NODES_LEFT = {
  ["parent"]: {
    [CURRICULUM_TYPE_PYP]: [
      widgetTypesConfig.PERSONAL_INFORMATION.id,
      widgetTypesConfig.CONTACT_INFORMATION.id,
      widgetTypesConfig.CLASSES.id,
      widgetTypesConfig.FAMILY_INFORMATION.id,
      widgetTypesConfig.SUBMISSION_INSIGHTS.id,
    ],
    [CURRICULUM_TYPE_MYP]: [
      widgetTypesConfig.PERSONAL_INFORMATION.id,
      widgetTypesConfig.CONTACT_INFORMATION.id,
      widgetTypesConfig.CLASSES.id,
      widgetTypesConfig.FAMILY_INFORMATION.id,
      widgetTypesConfig.SUBMISSION_INSIGHTS.id,
    ],
    [CURRICULUM_TYPE_UBD]: [
      widgetTypesConfig.PERSONAL_INFORMATION.id,
      widgetTypesConfig.CONTACT_INFORMATION.id,
      widgetTypesConfig.CLASSES.id,
      widgetTypesConfig.FAMILY_INFORMATION.id,
      widgetTypesConfig.SUBMISSION_INSIGHTS.id,
    ],
    [CURRICULUM_TYPE_DP]: [
      widgetTypesConfig.PERSONAL_INFORMATION.id,
      widgetTypesConfig.CONTACT_INFORMATION.id,
      widgetTypesConfig.CLASSES.id,
      widgetTypesConfig.FAMILY_INFORMATION.id,
      widgetTypesConfig.IB_ECOURSEWORK_OVERVIEW.id,
      widgetTypesConfig.SUBMISSION_INSIGHTS.id,
    ],
  },
  ["student"]: {
    [CURRICULUM_TYPE_PYP]: [
      widgetTypesConfig.PERSONAL_INFORMATION.id,
      widgetTypesConfig.CONTACT_INFORMATION.id,
      widgetTypesConfig.CLASSES.id,
      widgetTypesConfig.FAMILY_INFORMATION.id,
      widgetTypesConfig.SUBMISSION_INSIGHTS.id,
    ],
    [CURRICULUM_TYPE_MYP]: [
      widgetTypesConfig.PERSONAL_INFORMATION.id,
      widgetTypesConfig.CONTACT_INFORMATION.id,
      widgetTypesConfig.CLASSES.id,
      widgetTypesConfig.FAMILY_INFORMATION.id,
      widgetTypesConfig.SUBMISSION_INSIGHTS.id,
    ],
    [CURRICULUM_TYPE_UBD]: [
      widgetTypesConfig.PERSONAL_INFORMATION.id,
      widgetTypesConfig.CONTACT_INFORMATION.id,
      widgetTypesConfig.CLASSES.id,
      widgetTypesConfig.FAMILY_INFORMATION.id,
      widgetTypesConfig.SUBMISSION_INSIGHTS.id,
    ],
    [CURRICULUM_TYPE_DP]: [
      widgetTypesConfig.PERSONAL_INFORMATION.id,
      widgetTypesConfig.CONTACT_INFORMATION.id,
      widgetTypesConfig.CLASSES.id,
      widgetTypesConfig.FAMILY_INFORMATION.id,
      widgetTypesConfig.IB_ECOURSEWORK_OVERVIEW.id,
      widgetTypesConfig.SUBMISSION_INSIGHTS.id,
    ],
  },
  ["staff"]: {
    [CURRICULUM_TYPE_PYP]: [
      widgetTypesConfig.PERSONAL_INFORMATION.id,
      widgetTypesConfig.CONTACT_INFORMATION.id,
      widgetTypesConfig.CLASSES.id,
      widgetTypesConfig.FAMILY_INFORMATION.id,
      widgetTypesConfig.SUBMISSION_INSIGHTS.id,
    ],
    [CURRICULUM_TYPE_MYP]: [
      widgetTypesConfig.PERSONAL_INFORMATION.id,
      widgetTypesConfig.CONTACT_INFORMATION.id,
      widgetTypesConfig.CLASSES.id,
      widgetTypesConfig.FAMILY_INFORMATION.id,
      widgetTypesConfig.SUBMISSION_INSIGHTS.id,
      widgetTypesConfig.PERSONAL_PROJECT.id,
      widgetTypesConfig.COMMUNITY_PROJECT.id,
      widgetTypesConfig.SERVICE_AS_ACTION.id,
    ],
    [CURRICULUM_TYPE_UBD]: [
      widgetTypesConfig.PERSONAL_INFORMATION.id,
      widgetTypesConfig.CONTACT_INFORMATION.id,
      widgetTypesConfig.CLASSES.id,
      widgetTypesConfig.FAMILY_INFORMATION.id,
      widgetTypesConfig.SUBMISSION_INSIGHTS.id,
    ],
    [CURRICULUM_TYPE_DP]: [
      widgetTypesConfig.PERSONAL_INFORMATION.id,
      widgetTypesConfig.CONTACT_INFORMATION.id,
      widgetTypesConfig.CLASSES.id,
      widgetTypesConfig.FAMILY_INFORMATION.id,
      widgetTypesConfig.SUBMISSION_INSIGHTS.id,
      widgetTypesConfig.IB_ECOURSEWORK_OVERVIEW.id,
    ],
  },
};

export const WIDGET_TYPES_NODES_RIGHT = {
  ["parent"]: {
    [CURRICULUM_TYPE_PYP]: [
      widgetTypesConfig.HOMEROOMWIDGET.id,
      widgetTypesConfig.ATTENDANCE.id,
      widgetTypesConfig.TODO.id,
    ],
    [CURRICULUM_TYPE_MYP]: [
      widgetTypesConfig.HOMEROOMWIDGET.id,
      widgetTypesConfig.ATTENDANCE.id,
      widgetTypesConfig.TODO.id,
    ],
    [CURRICULUM_TYPE_UBD]: [
      widgetTypesConfig.HOMEROOMWIDGET.id,
      widgetTypesConfig.ATTENDANCE.id,
      widgetTypesConfig.TODO.id,
    ],
    [CURRICULUM_TYPE_DP]: [
      widgetTypesConfig.HOMEROOMWIDGET.id,
      widgetTypesConfig.ATTENDANCE.id,
      widgetTypesConfig.TODO.id,
    ],
  },
  ["student"]: {
    [CURRICULUM_TYPE_PYP]: [
      widgetTypesConfig.THIRD_PARTY_ACCOUNT.id,
      widgetTypesConfig.HOMEROOMWIDGET.id,
      widgetTypesConfig.ATTENDANCE.id,
      widgetTypesConfig.TODO.id,
    ],
    [CURRICULUM_TYPE_MYP]: [
      widgetTypesConfig.THIRD_PARTY_ACCOUNT.id,
      widgetTypesConfig.HOMEROOMWIDGET.id,
      widgetTypesConfig.ATTENDANCE.id,
      widgetTypesConfig.TODO.id,
    ],
    [CURRICULUM_TYPE_UBD]: [
      widgetTypesConfig.THIRD_PARTY_ACCOUNT.id,
      widgetTypesConfig.HOMEROOMWIDGET.id,
      widgetTypesConfig.ATTENDANCE.id,
      widgetTypesConfig.TODO.id,
    ],
    [CURRICULUM_TYPE_DP]: [
      widgetTypesConfig.THIRD_PARTY_ACCOUNT.id,
      widgetTypesConfig.HOMEROOMWIDGET.id,
      widgetTypesConfig.ATTENDANCE.id,
      widgetTypesConfig.TODO.id,
    ],
  },
  ["staff"]: {
    [CURRICULUM_TYPE_PYP]: [
      widgetTypesConfig.HOMEROOMWIDGET.id,
      widgetTypesConfig.ATTENDANCE.id,
      widgetTypesConfig.TODO.id,
    ],
    [CURRICULUM_TYPE_MYP]: [
      widgetTypesConfig.HOMEROOMWIDGET.id,
      widgetTypesConfig.ATTENDANCE.id,
      widgetTypesConfig.TODO.id,
    ],
    [CURRICULUM_TYPE_UBD]: [
      widgetTypesConfig.HOMEROOMWIDGET.id,
      widgetTypesConfig.ATTENDANCE.id,
      widgetTypesConfig.TODO.id,
    ],
    [CURRICULUM_TYPE_DP]: [
      widgetTypesConfig.HOMEROOMWIDGET.id,
      widgetTypesConfig.ATTENDANCE.id,
      widgetTypesConfig.TODO.id,
    ],
  },
};

export const getAllTabs = ({
  t,
  curriculumProgram,
  curriculumProgramId,
  syncServices,
  userType,
  showIBTab,
  attendanceRecordType,
  projectGroupCategoryIdByType,
  showDetailsTabForNonSyncOrg,
  behaviourNomenclatureConstants,
  showDetailsTabForIntegrations,
  studentAlertNomenclatureConstants,
  tabsNomenclatureData,
  isStaffAssociatedWithStudent = false,
}) => {
  const tabList = PROFILE_SECTION_NODES[userType][curriculumProgram];

  return _.reduce(
    tabList,
    (result, data) => {
      const {
        label,
        value,
        perms = {},
        otherCondition,
        controlParameters = {},
        getControlParametersConfig,
      } = typesConfig[data] || {};

      const controlParametersConfig = getControlParametersConfig
        ? getControlParametersConfig()
        : controlParameters;

      const { [userType]: permsByUserType = {} } = perms;

      const { permissions = [], moduleSettings = [] } = controlParametersConfig;
      const {
        [curriculumProgram]: permissionsByCurriculumAndUser,
      } = permsByUserType;

      let isAllowed = true;

      if (!_.isEmpty(controlParametersConfig)) {
        if (!_.isEmpty(permissions)) {
          isAllowed =
            isAllowed &&
            _.some(permissions, perm => ACLStorage.getPermissionValue(perm));
        }

        if (!_.isEmpty(moduleSettings)) {
          isAllowed =
            isAllowed &&
            _.some(moduleSettings, moduleSetting =>
              ACLStorage.getModuleSettingsValue(moduleSetting)
            );
        }
      }

      if (!_.isEmpty(permissionsByCurriculumAndUser)) {
        isAllowed =
          isAllowed && ACLStore.can(permissionsByCurriculumAndUser, "all");
      }

      isAllowed &= otherCondition
        ? otherCondition({
            syncServices,
            showIBTab,
            userType,
            curriculumProgram,
            projectGroupCategoryIdByType,
            curriculumProgramId,
            attendanceRecordType,
            showDetailsTabForNonSyncOrg,
            showDetailsTabForIntegrations,
            isStaffAssociatedWithStudent,
          })
        : true;

      if (isAllowed) {
        let tabLabel = t(label);
        switch (value) {
          case "behaviourRecord":
            tabLabel = behaviourNomenclatureConstants?.MODULE;
            break;
          case "student-alerts":
            tabLabel = t("studentAlert:student_alerts", {
              ...studentAlertNomenclatureConstants,
            });
            break;
          default: {
            const nomenclatureConfig = _.get(tabsNomenclatureData, value);
            if (nomenclatureConfig) {
              const {
                label: nomenclatureLabel,
                locale: nomenclatureLocale,
              } = nomenclatureConfig;

              tabLabel = nomenclatureLocale
                ? t(nomenclatureLocale)
                : nomenclatureLabel || tabLabel;
            }
            break;
          }
        }

        result.push({ label: tabLabel, value: value });
      }
      return result;
    },
    []
  );
};

/**
 * @description Utilities functions
 */

export const upComingDeadline = ({ deadLines, key }) => {
  const today = moment();

  const result = _.reduce(
    deadLines,
    (result, deadLine) => {
      const diffFromToday = today.diff(deadLine[key], "days");

      if (diffFromToday <= 0 && diffFromToday > result.MIN_DATE) {
        result.MIN_DATE = diffFromToday;
        result.deadLine = deadLine;
      }
      return result;
    },
    {
      MIN_DATE: -100000,
      deadLine: null,
    }
  );

  return result.deadLine;
};

export const shouldShowBirthPlace = () => {
  return ACLStore.can("FeatureFlag:StudentPortalBirthPlace");
};

export const shouldShowNationalId = () => {
  return ACLStore.can("FeatureFlag:StudentPortalNationalId");
};

export const shouldShowUID = () => {
  return ACLStore.can("FeatureFlag:EnableStudentUIDInStudentProfile");
};

export const canEditStudentUID = () => {
  const isUserAdmin = getIsUserAdminByRoleOrPermission();
  return shouldShowUID() && isUserAdmin;
};

export const shouldShowYearGroup = () =>
  ACLStore.can("StudentProfile:ShowYearGroupInPersonalInfo");

export const getOperationDayNumber = () => {
  const operationalDayFormatted = moment().locale("en").format("ddd");
  return _.indexOf(DAYS_IN_ORDER, operationalDayFormatted);
};

const plannerElementsFilter = ({ projectGroupType }) => {
  const subType =
    projectGroupType === widgetTypesConfig.PERSONAL_PROJECT.id
      ? "PERSONAL_OBJECTIVES"
      : "COMMUNITY_OBJECTIVES";
  return {
    subTypes: [subType],
  };
};

const getMappedPlannerElementSet = ({ set }) => {
  const rootData = _.filter(set, data => !data.isLeaf);

  return _.map(rootData, data => {
    return {
      ...data,
      label: _.replace(data.label, "Objective", "Criterion"),
    };
  });
};

export const shouldDisplayMiddleNameInPersonalInfo = () => {
  return ACLStore.can("FeatureFlag:EnableMiddleNameInPersonalInfo");
};

export const enableStudentMiddleName = () => {
  return ACLStore.can(ENABLE_STUDENT_MIDDLE_NAME);
};

export const shouldDisplayPronounsInPersonalInfo = () => {
  return (
    ACLStore.can("FeatureFlag:EnableStudentPronounsInStudentProfile") &&
    getIsStudentPronounsEnabled()
  );
};

export const shouldDisplayLanguagesInPersonalInfo = () => {
  return ACLStore.can("FeatureFlag:EnableLanguagesInPersonalInfoWidget");
};

export const shouldDisplayAdditionalFieldInPersonalInfo = field => {
  return ACLStore.can(field.displayPerm);
};

export const getLabelForPersonalInfoAdditionalField = field => {
  return parseConfigurationValue(ACLStore.getConfigObj(field.customLabelPerm));
};

//this function will check if last value in family modal is
//not empty and validate
export const shouldDisableAdd = ({ family = [] }) => {
  const lastData = family[family.length - 1];

  return (
    _.isEmpty(lastData.firstName) ||
    _.isEmpty(lastData.lastName) ||
    !validateEmail(lastData.email)
  );
};

//this function will check if all data correct in
//family modal so that to enable or disable send invite modal
export const isAllDataCorrect = ({ family = [] }) => {
  let correct = true;

  _.forEach(family, data => {
    const isInfoCorrect =
      !_.isEmpty(data.firstName) &&
      !_.isEmpty(data.lastName) &&
      validateEmail(data.email);

    correct &= isInfoCorrect;
  });

  //calculate size of local data
  //if size is zero than in that case also dont enable button
  const localDataSize = _.size(family);

  return correct && localDataSize;
};

//constants for cols and styles
const contactInfoCols = [
  {
    id: "name",
    type: "CUSTOM",
    locale: "common:name",
  },
  {
    id: "relationship",
    type: "CUSTOM",
    locale: "common:relationship",
  },
  {
    id: "email",
    type: "CUSTOM",
    locale: "common:email",
  },
  {
    id: "phoneNumber",
    type: "CUSTOM",
    locale: "common:phone_number_sentence_case",
  },
  {
    id: "secondPhoneNumber",
    type: "CUSTOM",
    locale: "common:second_phone_number",
    perm: [ENABLE_SECOND_NUMBER_IN_CONTACT_INFO],
  },
  {
    id: "thirdPhoneNumber",
    type: "CUSTOM",
    locale: "common:third_phone_number",
    perm: [ENABLE_THIRD_NUMBER_IN_CONTACT_INFO],
  },
  {
    id: "action",
    type: "CUSTOM",
  },
];

const familyInfoCols = [
  {
    id: "name",
    type: "CUSTOM",
    locale: "common:name",
  },
  {
    id: "email",
    type: "CUSTOM",
    locale: "common:email",
  },
  {
    id: "status",
    type: "CUSTOM",
    locale: "common:status",
  },
  {
    id: "chat",
    type: "CUSTOM",
  },
  {
    id: "action",
    type: "CUSTOM",
  },
];

//contact and family info column list
export const columnList = ({ type, isStudentOrParent }) => {
  const cols =
    type === widgetTypesConfig.FAMILY_INFORMATION.id
      ? familyInfoCols
      : contactInfoCols;

  return _.filter(cols, ({ id, perm }) => {
    if (!_.isEmpty(perm) && !ACLStore.can(perm)) {
      return false;
    }

    if (isStudentOrParent) {
      return !_.includes(["chat", "action"], id);
    }

    return true;
  });
};

export const columnStyle = ({ type, isStudentOrParent, version = 1 }) => {
  const colsStyle = {
    [widgetTypesConfig.FAMILY_INFORMATION.id]: {
      staff: {
        gridTemplateColumns: "25% 32% 28% 4% auto",
        gridColumnGap: "1%",
        padding: "8px 0px",
      },
      nonStaff: {
        gridTemplateColumns: "25% 32% 28% 4%",
        gridColumnGap: "2%",
        padding: "8px 0px",
      },
    },
    [widgetTypesConfig.CONTACT_INFORMATION.id]: {
      staff: {
        gridTemplateColumns: ACLStore.can([
          ENABLE_SECOND_NUMBER_IN_CONTACT_INFO,
          ENABLE_THIRD_NUMBER_IN_CONTACT_INFO,
        ])
          ? "20% 10% 15% 12% 12% 12% auto"
          : "24% 12% 25% 20% auto",
        gridColumnGap: "2%",
        padding: "8px 0px",
      },
      nonStaff: {
        gridTemplateColumns: ACLStore.can([
          ENABLE_SECOND_NUMBER_IN_CONTACT_INFO,
          ENABLE_THIRD_NUMBER_IN_CONTACT_INFO,
        ])
          ? "20% 10% 15% 15% 15% 15%"
          : "28% 14% 28% 22%",
        gridColumnGap: "2%",
        padding: "8px 0px",
      },
    },
  };

  const colsStyleV2 = {
    [widgetTypesConfig.FAMILY_INFORMATION.id]: {
      staff: {
        gridTemplateColumns: "30% 35% 20% 5% auto",
        gridColumnGap: "1%",
      },
      nonStaff: {
        gridTemplateColumns: "30% 35% 20% 5%",
        gridColumnGap: "2%",
      },
    },
    [widgetTypesConfig.CONTACT_INFORMATION.id]: {
      staff: {
        gridTemplateColumns: "24% 12% 25% 20% auto",
        gridColumnGap: "20px",
      },
      nonStaff: {
        gridTemplateColumns: "28% 14% 28% 22%",
        gridColumnGap: "2%",
      },
    },
  };

  const userTypeIdentifier = isStudentOrParent ? "nonStaff" : "staff";
  const styles = _.isEqual(version, 2) ? colsStyleV2 : colsStyle;
  return _.get(styles, `${type}.${userTypeIdentifier}`, {});
};

/**
 * @description memoized function
 */
export const getUpCompingDeadLineMemoized = _.memoize(
  params => upComingDeadline(params),
  params => JSON.stringify(params)
);

export const getPlannerElementsFilterMemoized = _.memoize(
  params => plannerElementsFilter(params),
  params => JSON.stringify(params)
);

export const getMappedPlannerElementSetMemoized = _.memoize(
  params => getMappedPlannerElementSet(params),
  params => JSON.stringify(params)
);

// exam reg constants
export const SHOW_WITH_PIN = "SHOW_WITH_PIN";
export const SHOW_WITHOUT_PIN = "SHOW_WITHOUT_PIN";

export const CODE_OF_CONDUCT_TABS = [
  { id: "CODE_OBJECTIVE", label: "codeofconduct:code_objective" },
  {
    id: "SCORECARD",
    label: "codeofconduct:score_card",
  },
  {
    id: "GAINS",
    label: "codeofconduct:distinguished_behaviour_assessment",
  },
  {
    id: "OFFENCES",
    label: "codeofconduct:offenses",
  },
];

export const buildGainTableTree = (gainTableData, childArray) => {
  if (_.isEmpty(childArray)) return [];
  return _.map(childArray, childId => {
    const node = _.cloneDeep(
      _.find(gainTableData, node => node.id === childId)
    );
    const childrenNodes = _.cloneDeep(
      buildGainTableTree(gainTableData, node.children)
    );
    return {
      id: node.id,
      label: node.label,
      children: childrenNodes,
      value: _.get(node, "metaData.value", 0),
    };
  });
};

const isTermAndCriteriaSetSame = ({
  selectedTermId,
  criteriaSetId,
  gainRating,
}) => {
  return (
    _.isEqual(selectedTermId, _.get(gainRating, "academicTerm.id", false)) &&
    _.isEqual(criteriaSetId, _.get(gainRating, "academicCriteriaSet.id", false))
  );
};

export const getSumCheckListAndMapping = ({
  academicCriteriaSets,
  gainRatings,
  selectedTermId,
}) => {
  let pointsSum = {};
  let selectedCheckList = {};
  let selectedCheckListMapping = {};

  _.forEach(academicCriteriaSets, criteriaSet => {
    let pointsSumThisCriteriaSet = 0;
    const criteriaSetId = _.get(criteriaSet, "id", "");
    const selectedCheckListThisCriteriaSet = _.reduce(
      gainRatings,
      (selectedCheckListThisCriteriaSetResult, gainRating) => {
        if (
          !isTermAndCriteriaSetSame({
            selectedTermId,
            criteriaSetId,
            gainRating,
          })
        ) {
          return selectedCheckListThisCriteriaSetResult;
        }

        pointsSumThisCriteriaSet += _.parseInt(_.get(gainRating, "value", "0"));

        const currentCriteriaSetMarkingKey = `${_.get(
          gainRating,
          "ibPYPElementId",
          ""
        )}_${criteriaSetId}`;
        return {
          ...selectedCheckListThisCriteriaSetResult,
          [currentCriteriaSetMarkingKey]: _.get(gainRating, "value", "0"),
        };
      },
      {}
    );
    pointsSum = { ...pointsSum, [criteriaSetId]: pointsSumThisCriteriaSet };
    selectedCheckList = {
      ...selectedCheckList,
      ...selectedCheckListThisCriteriaSet,
    };
    const selectedCheckListMappingThisCriteriaSet = _.reduce(
      gainRatings,
      (selectedCheckListMappingThisCriteriaSetResult, gainRating) => {
        if (
          !isTermAndCriteriaSetSame({
            selectedTermId,
            criteriaSetId,
            gainRating,
          })
        ) {
          return selectedCheckListMappingThisCriteriaSetResult;
        }

        const currentCriteriaSetGainRatingKey = `${_.get(
          gainRating,
          "ibPYPElementId",
          ""
        )}_${criteriaSetId}`;

        return {
          ...selectedCheckListMappingThisCriteriaSetResult,
          [currentCriteriaSetGainRatingKey]: _.get(gainRating, "id", ""),
        };
      },
      {}
    );
    selectedCheckListMapping = {
      ...selectedCheckListMapping,
      ...selectedCheckListMappingThisCriteriaSet,
    };
  });

  return {
    pointsSum,
    selectedCheckList,
    selectedCheckListMapping,
  };
};

export const getScoresForTheTerm = ({
  term,
  academicCriteriaSets,
  termsOffenceScoreSum,
  codeOfConductDeductions,
  gainRatings,
  maxPositiveBehaviour,
}) => {
  return _.reduce(
    academicCriteriaSets,
    (scoresResult, criteriaSet) => {
      const criteriaSetId = _.get(criteriaSet, "id", "");
      const offenceScoreSumObject = _.find(termsOffenceScoreSum, termScore =>
        _.isEqual(_.get(term, "id", true), _.get(termScore, "termId", false))
      );

      const overridenOffenceScoreObject = _.find(
        codeOfConductDeductions,
        item =>
          _.isEqual(
            _.get(term, "id", true),
            _.get(item, "academicTerm.id", false)
          ) &&
          _.isEqual(criteriaSetId, _.get(item, "academicCriteriaSet.id", false))
      );

      const positivePointsSum = _.reduce(
        gainRatings,
        (result, gainRating) => {
          if (
            !_.isEqual(
              _.get(term, "id", true),
              _.get(gainRating, "academicTerm.id", false)
            ) ||
            !_.isEqual(
              criteriaSetId,
              _.get(gainRating, "academicCriteriaSet.id", false)
            )
          )
            return result;
          else return result + _.parseInt(_.get(gainRating, "value", "0"));
        },
        0
      );

      const offenceScoreSumTotal = _.get(
        offenceScoreSumObject,
        `offenceScoreSum.${criteriaSetId}`,
        0
      );

      let offenceScoreSum = offenceScoreSumTotal;

      if (_.size(overridenOffenceScoreObject)) {
        const overridenOffenceValue = _.get(
          overridenOffenceScoreObject,
          "value",
          ""
        );
        if (_.size(overridenOffenceValue)) {
          offenceScoreSum = _.toInteger(overridenOffenceValue);
        }
      }

      const dueOffenceScoreSum = maxPositiveBehaviour - offenceScoreSum;
      const duePositivePointsSum = _.toInteger(positivePointsSum / 4);

      return {
        ...scoresResult,
        [criteriaSetId]: {
          offenceScoreSum,
          dueOffenceScoreSum,
          duePositivePointsSum,
        },
      };
    },
    {}
  );
};

export const getOffenceTermScoreArr = row => {
  return _.map(row.scores, score => {
    return score?.dueOffenceScoreSum;
  });
};

export const getPositiveTermScoreArr = row => {
  return _.map(row.scores, score => {
    return score?.duePositivePointsSum;
  });
};

export const getStudentProfileEditAccess = ({
  isTeacherCourseStudent = false,
  isFromOverviewWidget = false,
}) => {
  if (ACLStore.can(ENABLE_ROLES_AND_PERMISSIONS_CONSUMPTION)) {
    return ACLStorage.getPermissionValue(
      STUDENT_PROFILE_CONTROL_PARAMETER.UPDATE_DETAILS
    );
  }

  // TODO: Remove isAdmin related check once RnP is enabled globally
  const isAdminByRole = getIsAdminByRole();
  const isAdminByRoleOrPermission = getIsUserAdminByRoleOrPermission();

  const isOverviewEditDisabledForNonAdmins = ACLStore.can(
    "FeatureFlag:DisableOverviewEditForTeachers"
  );

  if (
    !isAdminByRoleOrPermission &&
    isFromOverviewWidget &&
    isOverviewEditDisabledForNonAdmins
  ) {
    return false;
  }

  if (
    !isAdminByRole &&
    ACLStore.isStaffPermissionEnabled(MODULES.STUDENT_PROFILE.id)
  ) {
    const { isAdminAccess, isTeacherAccess } = getAccessLevels({
      [MODULES.STUDENT_PROFILE.id]: {
        isAdminAccess: {
          rule: RULE_OPTIONS.eq,
          value: MODULES.STUDENT_PROFILE.values.ADMIN_ACCESS.id,
          defaultAccessForUserBeingAdmin: true,
          defaultAccess: true,
        },
        isTeacherAccess: {
          rule: RULE_OPTIONS.eq,
          value: MODULES.STUDENT_PROFILE.values.TEACHER_ACCESS.id,
          defaultAccess: true,
        },
      },
    });

    return isAdminAccess || (isTeacherAccess && isTeacherCourseStudent);
  }

  return true;
};

export const getHomeroomAndClassesEditAccess = () => {
  // TODO: Remvoe this check once RnP is enabled globally
  if (!ACLStore.can(ENABLE_ROLES_AND_PERMISSIONS_CONSUMPTION)) {
    return getIsUserAdminByRoleOrPermission();
  }

  return ACLStorage.getPermissionValue(
    STUDENT_PROFILE_CONTROL_PARAMETER.UPDATE_HOMEROOM_AND_CLASSES
  );
};

const isUserRoleAdmin = hasModuleAdminAccess({
  moduleName: MODULES.ATTENDANCE.id,
});

export const getManageExcusalsPermission = () => {
  if (!ACLStore.can(ENABLE_ROLES_AND_PERMISSIONS_CONSUMPTION)) {
    return isUserRoleAdmin;
  }
  return ACLStorage.getPermissionValue(
    STUDENT_PROFILE_CONTROL_PARAMETER.MANAGE_EXCUSALS
  );
};

export const getShowStudentUIDInStudentProfile = () =>
  ACLStore.can("FeatureFlag:ShowStudentUIDInStudentProfile");

export const getIsUserAdminByRoleOrPermission = () => {
  const isAdminByRole = getIsAdminByRole();

  const { isAdminByRoleOrPermission } = getAccessLevels({
    [MODULES.STUDENT_PROFILE.id]: {
      isAdminByRoleOrPermission: {
        rule: RULE_OPTIONS.eq,
        value: MODULES.STUDENT_PROFILE.values.ADMIN_ACCESS.id,
        defaultAccessForUserBeingAdmin: true,
        defaultAccessForPermissionNotEnabled: isAdminByRole,
        defaultAccess: false,
      },
    },
  });

  return isAdminByRoleOrPermission;
};

const canAdminsEditTeacherNotesDescriptionFlag = () =>
  ACLStore.can("FeatureFlag:AdminsCanEditTeacherNoteDescription");

export const canAdminEditTeacherNotesDescription = () => {
  let isAdminTypeUser = ACLStorage.getModuleSettingsValueByContext(
    ADMIN_PORTAL_ACCESS_PERMISSION,
    []
  );

  // TODO: Remove this check once RnP is enabled globally
  if (!ACLStore.can(ENABLE_ROLES_AND_PERMISSIONS_CONSUMPTION)) {
    isAdminTypeUser = isAdmin();
  }

  return isAdminTypeUser && canAdminsEditTeacherNotesDescriptionFlag();
};

/**
 * Retrieves additional fields data of student.
 * @param {Object} param - parameter object.
 * @param {Object} param.studentOtherDetails - othreDeatils field of the student.
 * @returns {Array<{value, label, fieldName}>} - Array of additionalInfo objects.
 */
export const getStudentAdditionalFieldsData = ({
  studentOtherDetails = {},
} = {}) => {
  const testId = "additionalFields";

  const ADDITIONAL_FIELDS_MAPPING = [
    {
      testId: `${testId}-firstAdditionalField`,
      perm: PROFILE_ADDITIONAL_FIELDS.firstAdditionalField,
      value: studentOtherDetails.firstNameLocale,
      fieldName: "firstNameLocale",
      fieldNo: "firstAdditionalField",
    },
    {
      testId: `${testId}-secondAdditionalField`,
      perm: PROFILE_ADDITIONAL_FIELDS.secondAdditionalField,
      value: studentOtherDetails.lastNameLocale,
      fieldName: "lastNameLocale",
      fieldNo: "secondAdditionalField",
    },
    {
      testId: `${testId}-thirdAdditionalField`,
      perm: PROFILE_ADDITIONAL_FIELDS.thirdAdditionalField,
      value: studentOtherDetails.birthPlaceLocale,
      fieldName: "birthPlaceLocale",
      fieldNo: "thirdAdditionalField",
    },
    {
      testId: `${testId}-fourthAdditionalField`,
      perm: PROFILE_ADDITIONAL_FIELDS.fourthAdditionalField,
      value: studentOtherDetails.dobLocale,
      fieldName: "dobLocale",
      fieldNo: "fourthAdditionalField",
    },
    {
      testId: `${testId}-fifthAdditionalField`,
      perm: PROFILE_ADDITIONAL_FIELDS.fifthAdditionalField,
      value: studentOtherDetails.religionLocale,
      fieldName: "religionLocale",
      fieldNo: "fifthAdditionalField",
    },
    {
      testId: `${testId}-sixthAdditionalField`,
      perm: PROFILE_ADDITIONAL_FIELDS.sixthAdditionalField,
      value: studentOtherDetails.primaryNationalityLocale,
      fieldName: "primaryNationalityLocale",
      fieldNo: "sixthAdditionalField",
    },
    {
      testId: `${testId}-seventhAdditionalField`,
      perm: PROFILE_ADDITIONAL_FIELDS.seventhAdditionalField,
      value: studentOtherDetails.seventhAdditionalField,
      fieldName: "seventhAdditionalField",
      fieldNo: "seventhAdditionalField",
    },
    {
      testId: `${testId}-eighthAdditionalField`,
      perm: PROFILE_ADDITIONAL_FIELDS.eighthAdditionalField,
      value: studentOtherDetails.eighthAdditionalField,
      fieldName: "eighthAdditionalField",
      fieldNo: "eighthAdditionalField",
    },
  ];

  const filteredAdditionalFields = _.filter(
    ADDITIONAL_FIELDS_MAPPING,
    ({ perm }) => {
      return shouldDisplayAdditionalFieldInPersonalInfo(perm);
    }
  );

  const fieldConfig = getStudentProfileCustomFieldsConfig();

  return _.map(filteredAdditionalFields, additionalInfo => {
    return {
      ...additionalInfo,
      ..._.get(fieldConfig, additionalInfo.fieldNo, {}),
      label: getLabelForPersonalInfoAdditionalField(additionalInfo.perm),
    };
  });
};

const getHasAccessToNavigateToClasses = ({
  courseId,
  collaborators,
  isDemo,
  curriculumCourses,
}) => {
  const userInfo = getUserInfo();
  const { id, user_type, isDevUser } = userInfo;

  let hasAccess = false;

  if (isDemo || isDevUser) {
    hasAccess = true;
  } else {
    if (user_type != "staff") {
      hasAccess = true;
    } else {
      const isCourseInSelectedAcademicYear = _.includes(
        _.map(curriculumCourses, course => course.id),
        courseId
      );

      const collaboratorEdges = _.get(collaborators, "edges", []);
      const hasMatchingCollaborator = _.some(
        collaboratorEdges,
        ({ node }) => node.id == id
      );
      hasAccess = hasMatchingCollaborator && isCourseInSelectedAcademicYear;
    }
  }

  return hasAccess;
};
export const getHasAccessToNavigateToClassesMemoized = _.memoize(
  params => getHasAccessToNavigateToClasses(params),
  params => JSON.stringify(params)
);

export const getIsClassNavigationEnabledFromStudentProfile = () =>
  ACLStore.can("FeatureFlag:EnableClassNavFromStudentProfile");

const getUserCoursesFiltersObj = ({
  currentCurriculumProgramType,
  currentCurriculumProgramId,
  organizationCurriculumPrograms,
  isCurrentAcademicYear,
  academicYear,
  userTags,
}) => {
  return {
    ...getUserCoursesFilters({
      currentCurriculumProgramType,
      currentCurriculumProgramId,
      organizationCurriculumPrograms,
    }),
    ...(!isCurrentAcademicYear && { academicYear }),
    ...getUserCourseTagsFilters({ userTags }),
  };
};

export const getUserCoursesFiltersObjMemoized = _.memoize(
  params => getUserCoursesFiltersObj(params),
  params => JSON.stringify(params)
);

//// --- Widget visibility helper functions --- ////
const isCurrentCurriculumPermitted = ({
  currentCurriculum,
  allowedCurriculum,
}) => {
  return !_.isEmpty(_.intersection(allowedCurriculum, [currentCurriculum]));
};

const isCurriculumWisePermissionAllowed = ({
  permsByCurriculumAndUserType,
  userType,
  currentCurriculumProgramType,
}) => {
  const { [userType]: permByUserType = {} } = permsByCurriculumAndUserType;

  const {
    [currentCurriculumProgramType]: permByCurriculumAndUser,
  } = permByUserType;

  let isAllowed =
    !_.isEmpty(permByUserType) && permByCurriculumAndUser !== undefined;

  isAllowed &= !_.size(permByCurriculumAndUser)
    ? true
    : ACLStore.can(permByCurriculumAndUser, "all");

  return isAllowed;
};

//// --- Widget visibility function --- ////
export const isWidgetVisible = ({
  type,
  currentCurriculumProgramType,
  userType,
  projectGroupCategoryIdByType,
  currentCurriculumProgramId,
}) => {
  const {
    perms = [],
    curriculumPrograms = [],
    permsByCurriculumAndUserType = {},
    controlParameters = {},
    otherCondition,
  } = widgetTypesConfig[type];

  /**
   * refactored and cleaned code
   * logic : if size of permission and curriculum both there then check both , else check any of one
   */
  let isAllowed = true;
  if (!_.isEmpty(controlParameters)) {
    const {
      permissions = [],
      moduleSettings = [],
      projectGroupType,
    } = controlParameters;

    if (!_.isEmpty(permissions)) {
      isAllowed =
        isAllowed &&
        _.some(permissions, perm => ACLStorage.getPermissionValue(perm));
    }

    if (projectGroupType) {
      const projectGroupCategoryId = _.get(
        projectGroupCategoryIdByType,
        projectGroupType
      );
      isAllowed =
        isAllowed &&
        ACLStorage.getModuleSettingsValueByContext(
          _.first(moduleSettings),
          getProjectGroupContexts({
            projectGroupCategoryId,
            curriculumProgramId: currentCurriculumProgramId,
          })
        );
    } else if (!_.isEmpty(moduleSettings)) {
      isAllowed =
        isAllowed &&
        _.some(moduleSettings, moduleSetting =>
          ACLStorage.getModuleSettingsValue(moduleSetting)
        );
    }

    if (!_.isEmpty(perms)) {
      const hasDisableFamilyInviteOption = _.includes(
        perms,
        DISABLE_FAMILY_INVITE_OPTIONS
      );

      if (hasDisableFamilyInviteOption) {
        isAllowed = isAllowed && !ACLStore.can(perms, "all");
      } else {
        isAllowed = isAllowed && ACLStore.can(perms, "all");
      }
    } else if (!_.isEmpty(permsByCurriculumAndUserType)) {
      isAllowed =
        isAllowed &&
        isCurriculumWisePermissionAllowed({
          permsByCurriculumAndUserType,
          userType,
          currentCurriculumProgramType,
        });
    }

    if (!_.isEmpty(curriculumPrograms)) {
      isAllowed =
        isAllowed &&
        isCurrentCurriculumPermitted({
          currentCurriculum: currentCurriculumProgramType,
          allowedCurriculum: curriculumPrograms,
        });
    }
  } else if (!_.isEmpty(perms)) {
    isAllowed = isAllowed && ACLStore.can(perms, "all");
    if (!_.isEmpty(curriculumPrograms)) {
      isAllowed =
        isAllowed &&
        isCurrentCurriculumPermitted({
          currentCurriculum: currentCurriculumProgramType,
          allowedCurriculum: curriculumPrograms,
        });
    }
  } else if (!_.isEmpty(permsByCurriculumAndUserType)) {
    isAllowed =
      isAllowed &&
      isCurriculumWisePermissionAllowed({
        permsByCurriculumAndUserType,
        userType,
        currentCurriculumProgramType,
      });
  } else if (!_.isEmpty(curriculumPrograms)) {
    isAllowed =
      isAllowed &&
      isCurrentCurriculumPermitted({
        currentCurriculum: currentCurriculumProgramType,
        allowedCurriculum: curriculumPrograms,
      });
  }

  if (_.isFunction(otherCondition)) {
    isAllowed = isAllowed && otherCondition({ userType });
  }

  return isAllowed;
};

export const getIsStudentProfileUIRevampEnabled = () => {
  return ACLStore.can(ENABLE_STUDENT_PROFILE_UI_REVAMP);
};

export const getNoteCategoryTagColor = category => {
  const NOTE_CATEGORY_COLORS = {
    pink: {
      color: "rgba(243, 132, 133, 1)",
      backgroundColor: "rgba(243, 132, 133, 0.2)",
    },
    violet: {
      color: "rgba(100, 100, 220, 1)",
      backgroundColor: "rgba(100, 100, 220, 0.2)",
    },
    yellow: {
      color: "rgba(255, 180, 0, 1)",
      backgroundColor: "rgba(255, 180, 0, 0.2)",
    },
  };

  const metadata = _.get(category, "metadata", {});

  const matchedEntry = _.find(NOTE_CATEGORY_COLORS, value =>
    _.isEqual(value, {
      color: metadata.color,
      backgroundColor: metadata.backgroundColor,
    })
  );
  return (
    _.findKey(NOTE_CATEGORY_COLORS, value => _.isEqual(value, matchedEntry)) ||
    "yellow"
  );
};

export const TEACHER_NOTE_MODAL_MODES = {
  NEW: "NEW",
  EDIT: "EDIT",
};

export const TEACHER_NOTE_ACTION = {
  EDIT: "EDIT",
  DELETE: "DELETE",
};

const getStateWiseCountVariables = params => {
  const {
    organizationId,
    studentId,
    academicYearId,
    isoStartDate,
    isoEndDate,
    curriculumProgramId,
  } = params;
  return {
    id: organizationId,
    input: {
      filters: [
        {
          type: BEHAVIOUR_FILTER_TYPE.IS,
          involvedStudents: [studentId],
          incidentTime: {
            startDate: isoStartDate,
            endDate: isoEndDate,
          },
        },
      ],
      academicYearId,
      curriculumProgramId,
    },
  };
};

export const getStateWiseCountVariablesMemoized = _.memoize(
  getStateWiseCountVariables,
  ({
    organizationId,
    studentId,
    academicYearId,
    isoStartDate,
    isoEndDate,
    curriculumProgramId,
  }) =>
    JSON.stringify({
      organizationId,
      studentId,
      academicYearId,
      isoStartDate,
      isoEndDate,
      curriculumProgramId,
    })
);

//// --- Student enrollment date utility functions --- ////

// If no withdrawal date, use today's date
export const getEnrollmentDateMaxDate = ({
  withdrawalDate,
  academicYears,
  selectedAcademicYearId,
}) => {
  const currentAcademicYear = _.find(academicYears, "isCurrentAcademicYear");
  const isCurrentAcademicYearSelected = _.isEqual(
    selectedAcademicYearId,
    _.get(currentAcademicYear, "id", null)
  );
  const yearEndDate = _.get(currentAcademicYear, "endDate", null);

  if (!isCurrentAcademicYearSelected) {
    return withdrawalDate ? moment(withdrawalDate) : null;
  }

  return yearEndDate ? moment(yearEndDate) : null;
};
